// app/javascript/controllers/tk_time_picker_controller.js
import { Controller } from "@hotwired/stimulus";

// 時刻入力(HH:MM)を、テキスト入力+候補パネルのUIに拡張するcontroller。
// vendor/tk-time-picker.js のロジックをこのcontroller単体に統合したもの。
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 30 },
    order: { type: String, default: "asc" },
    start: { type: String, default: "00:00" }
  };

  connect() {
    console.log("tkTimePickerController connected");
    this.times = this.generateTimes();
    this.buildDom();
    this.bindEvents();
    this.renderOptions();
  }

  disconnect() {
    this.unbindEvents();
    this.toggleButton?.remove();
    this.panel?.remove();
    this.wrapper?.classList.remove("tk-time-picker");
  }

  // ---- 時刻生成 ----

  generateTimes() {
    const times = [];
    const start = this.isValidTime(this.startValue)
      ? this.toMinutes(this.startValue)
      : 0;
    for (let minute = start; minute <= 1439; minute += this.intervalValue) {
      times.push(this.fromMinutes(minute));
    }
    return this.orderValue === "desc" ? times.reverse() : times;
  }

  isValidTime(value) {
    return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(String(value));
  }

  toMinutes(value) {
    const [h, m] = value.split(":").map(Number);
    return h * 60 + m;
  }

  fromMinutes(value) {
    const h = String(Math.floor(value / 60)).padStart(2, "0");
    const m = String(value % 60).padStart(2, "0");
    return `${h}:${m}`;
  }

  filterTimes(query) {
    const normalized = String(query || "")
      .trim()
      .replace(/:/g, "");
    if (!normalized) return [...this.times];
    return this.times.filter((time) =>
      time.replace(":", "").includes(normalized)
    );
  }

  // ---- DOM構築 ----

  buildDom() {
    // this.element は動かさない。親要素を「見た目上のwrapper」として使う。
    this.element.classList.add("tk-time-input");
    this.element.type = "text";
    this.element.autocomplete = "off";
    this.element.setAttribute("role", "combobox");
    this.element.setAttribute("aria-autocomplete", "list");
    this.element.setAttribute("aria-expanded", "false");

    // 見た目のためのpositioning用クラスを親に付与(すでにwrapper的なtd等がある前提)
    this.element.parentElement.classList.add("tk-time-picker");
    this.wrapper = this.element.parentElement;

    this.toggleButton = document.createElement("button");
    this.toggleButton.type = "button";
    this.toggleButton.className = "tk-time-toggle";
    this.toggleButton.setAttribute("aria-label", "時刻候補を開く");
    this.toggleButton.innerHTML =
      '<span class="tk-time-chevron" aria-hidden="true"></span>';

    this.panel = document.createElement("div");
    this.panel.className = "tk-time-panel";
    this.panel.hidden = true;

    this.list = document.createElement("div");
    this.list.className = "tk-time-options";
    this.list.setAttribute("role", "listbox");

    this.panel.appendChild(this.list);

    // element の直後にボタンとパネルを挿入する(elementのappendChildは使わない)
    this.element.insertAdjacentElement("afterend", this.panel);
    this.element.insertAdjacentElement("afterend", this.toggleButton);
  }

  // ---- イベント ----

  bindEvents() {
    this.onFocus = () => this.open();
    this.onInput = () => {
      this.renderOptions();
      this.open();
    };
    this.onKeyDown = (event) => {
      if (event.key === "Escape") {
        this.close();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        this.open();
        this.moveActive(event.key === "ArrowDown" ? 1 : -1);
      }
      if (event.key === "Enter" && this.activeIndex >= 0) {
        const options = this.visibleOptionButtons();
        if (options[this.activeIndex]) {
          event.preventDefault();
          this.choose(options[this.activeIndex].dataset.value);
        }
      }
    };
    this.onFocusOut = (event) => {
      if (!this.wrapper.contains(event.relatedTarget)) this.close();
    };
    this.onTogglePointerDown = (event) => event.preventDefault();
    this.onToggleClick = () => {
      if (this.isOpen()) this.close();
      else {
        this.element.focus();
        this.open();
      }
    };

    this.element.addEventListener("focus", this.onFocus);
    this.element.addEventListener("input", this.onInput);
    this.element.addEventListener("keydown", this.onKeyDown);
    this.wrapper.addEventListener("focusout", this.onFocusOut);
    this.toggleButton.addEventListener("pointerdown", this.onTogglePointerDown);
    this.toggleButton.addEventListener("click", this.onToggleClick);
  }

  unbindEvents() {
    this.element.removeEventListener("focus", this.onFocus);
    this.element.removeEventListener("input", this.onInput);
    this.element.removeEventListener("keydown", this.onKeyDown);
    this.wrapper?.removeEventListener("focusout", this.onFocusOut);
    this.toggleButton?.removeEventListener(
      "pointerdown",
      this.onTogglePointerDown
    );
    this.toggleButton?.removeEventListener("click", this.onToggleClick);
  }

  // ---- 描画 ----

  renderOptions() {
    const filtered = this.filterTimes(this.element.value);
    this.list.replaceChildren();
    this.activeIndex = -1;

    filtered.forEach((time) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tk-time-option";
      button.tabIndex = -1;
      button.dataset.value = time;
      button.setAttribute("role", "option");
      button.setAttribute(
        "aria-selected",
        this.element.value === time ? "true" : "false"
      );
      button.textContent = time;
      button.addEventListener("pointerdown", (event) => event.preventDefault());
      button.addEventListener("click", () => this.choose(time));
      this.list.appendChild(button);
    });

    if (!filtered.length) {
      const empty = document.createElement("p");
      empty.className = "tk-time-empty";
      empty.textContent = "候補にありません。HH:MM形式なら入力できます";
      this.list.appendChild(empty);
    }
  }

  visibleOptionButtons() {
    return Array.from(this.list.querySelectorAll(".tk-time-option"));
  }

  moveActive(direction) {
    const options = this.visibleOptionButtons();
    if (!options.length) return;
    this.activeIndex =
      (this.activeIndex + direction + options.length) % options.length;
    options.forEach((option, index) =>
      option.classList.toggle("is-active", index === this.activeIndex)
    );
    options[this.activeIndex].scrollIntoView({ block: "nearest" });
  }

  choose(value) {
    this.element.value = value;
    this.renderOptions();
    this.close();
    this.element.focus();
    this.element.dispatchEvent(new Event("input", { bubbles: true }));
    this.element.dispatchEvent(new Event("change", { bubbles: true }));
  }

  open() {
    if (this.element.disabled) return;
    this.wrapper.classList.add("is-open");
    this.panel.hidden = false;
    this.element.setAttribute("aria-expanded", "true");
  }

  close() {
    this.wrapper.classList.remove("is-open");
    this.panel.hidden = true;
    this.element.setAttribute("aria-expanded", "false");
  }

  isOpen() {
    return this.wrapper.classList.contains("is-open");
  }
}
